import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { 
  Save, Search, Plus, Trash2, ArrowLeft, List, Paperclip, History, 
  ChevronRight, ChevronLeft, Tag, Briefcase, FileText, UserCheck, 
  Wrench, Package, Box, Type, Minus 
} from 'lucide-react';
import { useTranslation } from 'react-i18next'; 

import UniversalLookup from './UniversalLookup'; 
import AttachmentsModal from './AttachmentsModal';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const JPDetails = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

  const { id } = useParams();
  const navigate = useNavigate();
  const locationPath = useLocation(); 
  const isNew = !id || id === 'new';
  
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const changeFontSize = fontContext?.changeFontSize || (() => {});

  const [activeTab, setActiveTab] = useState('Job Plan');
  const [planSubTab, setPlanSubTab] = useState('Tasks');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [count, setCount] = useState(0); 
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  
  const { canCreate, canUpdate, canDelete } = usePermissions('jobplans');

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  const [data, setData] = useState({
    jpnum: '', description: '', status: 'DRAFT', revision: 0,
    org_id: sessionOrg, site_id: sessionSite, duration: 1.0, template_type: 'Maintenance',
    supervisor: '', classstructureid: '', safetypnum: '',
    tasks: [], labor: [], materials: [], services: [], tools: [], woassets: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null });
  
  // 🎯 تظبيط الصلاحيات (Read-Only Logic)
  const isStatusReadOnly = ['APPROVED', 'APPR', 'CLOSE', 'CAN', 'CLOSED'].includes(data.status);
  const isReadOnly = (!isNew && !canUpdate) || isStatusReadOnly;
  const canSave = isNew ? canCreate : (canUpdate && !isStatusReadOnly);

  // 1. جلب البيانات
  const loadData = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`/api/jobplans/${id}`);
      setData(res.data);
    } catch (err) { toast.error(t('jp.details.load_error', "Error loading Job Plan")); }
  }, [id, isNew, t]);

  useEffect(() => { loadData(); }, [loadData]);

  // جلب النافيجيشن بالفلتر
  const loadNavigation = useCallback(async () => {
    const token = localStorage.getItem('token');
      if (isNew) return;
      try {
          const searchParams = locationPath.search;
          const res = await axios.get(`/api/jobplans/${id}/navigation${searchParams}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setNav(res.data);
      } catch (err) { console.error("Nav load failed"); }
  }, [id, isNew, locationPath.search]);
  
  useEffect(() => { loadNavigation(); }, [loadNavigation]);
  
  const goTo = (targetId) => { if (targetId) navigate(`/jp/${targetId}${locationPath.search}`); };

  useEffect(() => {
    if (isNew && data.org_id) {
        setData(prev => ({
          ...prev, description: '', status: 'DRAFT',
          site_id: session.site_id || '', org_id: session.org_id || '', safetypnum: '',
          duration: 1.0, template_type: 'Maintenance', supervisor: '', classstructureid: '',
          tasks: [], labor: [], materials: [], services: [], tools: [], woassets: []
        }));        
        axios.get(`/api/jp/next-number/${data.org_id}`)
            .then(res => { setData(prev => ({ ...prev, jpnum: res.data.next_jpnum })); });
    }
  }, [isNew, data.org_id, session.site_id, session.org_id]);
  
  const fetchCount = useCallback(async () => {
    if (!data.jpnum || isNew) { setCount(0); return; }
    try {
      const res = await axios.get(`/api/attachments/count/JOBPLAN/${data.jpnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error(err); }
  }, [data.jpnum, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  const handleSearch = (fieldName, title, defaultType, lineIdx = null) => {
    if (isReadOnly) return; // منع فتح البحث لو الشاشة مقفولة
    let extraFilters = { site_id: data.site_id, org_id: data.org_id };
    setLookup({ isOpen: true, title, type: defaultType, field: fieldName, filters: extraFilters, targetIdx: lineIdx });
  };

  const handleLookupSelect = (row) => {
    const val = row.value || row.itemnum || row.safetypnum || row.laborcode || row.location_id || row.id || row.assetnum || row.craft_code;
    
    if (lookup.field === 'row_craft_role' && lookup.targetIdx !== null) {
      updateSubRow('labor', lookup.targetIdx, 'craft', row.craft_code || val);
    } else if (lookup.field === 'row_labor_role' && lookup.targetIdx !== null) {
      updateSubRow('labor', lookup.targetIdx, 'laborcode', row.laborcode || val); 
      updateSubRow('labor', lookup.targetIdx, 'laborrate', row.actual_rate || val);     
    } else if (lookup.field === 'row_material_item' && lookup.targetIdx !== null) {
      updateSubRow('materials', lookup.targetIdx, 'itemnum', row.itemnum || val);
      updateSubRow('materials', lookup.targetIdx, 'unitcost', row.avgcost || 0);
    } else if (lookup.field === 'row_tool_num' && lookup.targetIdx !== null) {
      updateSubRow('tools', lookup.targetIdx, 'itemnum', row.itemnum || val);
    } else if (lookup.field === 'row_asset_num' && lookup.targetIdx !== null) {
      updateSubRow('woassets', lookup.targetIdx, 'assetnum', row.assetnum || val);
      if (row.location) updateSubRow('woassets', lookup.targetIdx, 'location', row.location_id);
    } else if (lookup.field === 'row_asset_loc' && lookup.targetIdx !== null) {
      updateSubRow('woassets', lookup.targetIdx, 'location', row.location_id || val);
    } else {
      setData({ ...data, [lookup.field]: val });
    }
    setLookup({ ...lookup, isOpen: false });
  };

  const updateSubRow = (tab, idx, field, value) => {
    if (isReadOnly) return;
    const newList = [...data[tab]];
    newList[idx][field] = value;
    if (tab === 'materials' && (field === 'quantity' || field === 'unitcost')) {
      newList[idx].linecost = (newList[idx].quantity || 0) * (newList[idx].unitcost || 0);
    }
    setData({ ...data, [tab]: newList });
  };

  const addSubRow = (tab) => {
    if (isReadOnly) return;
    const schemas = {
      tasks: { task_sequence: (data.tasks.length + 1) * 10, description: '', duration: 1.0 },
      labor: { laborcode: '', craft: '', quantity: 1, laborhrs: 1.0 },
      materials: { itemnum: '', quantity: 1, unitcost: 0, linecost: 0 },
      services: { itemnum: '', description: '', quantity: 1, unitcost: 0 },
      tools: { itemnum: '', toolhrs: 1.0, quantity: 1 },
      woassets: { assetnum: '', location: '', site_id: data.site_id }
    };
    setData({ ...data, [tab]: [...data[tab], schemas[tab]] });
  };

  const handleSave = async () => {
    if (!canSave) return;
    const loadId = toast.loading(t('common.saving', "Saving..."));
    try {
      await axios.post('/api/jobplans/save', data);
      toast.success(t('common.save_success', "Saved Successfully!"), { id: loadId });
      if (isNew) navigate(`/jp/${data.jpnum}${locationPath.search}`);
    } catch (err) { toast.error(t('common.save_fail', "Save Failed"), { id: loadId }); }
  };

  const handleDelete = async () => {
    if (!canDelete || isStatusReadOnly) return;
    const result = await Swal.fire({
        title: t('common.are_you_sure', 'Are you sure?'),
        text: t('jp.details.delete_warn', { jpnum: data.jpnum }),
        icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
        confirmButtonText: t('common.yes_delete', 'Yes, delete it!'),
        cancelButtonText: t('common.cancel', 'Cancel'),
        customClass: { popup: 'font-sans text-[12px]' }
    });

    if (result.isConfirmed) {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/jobplans/${data.jpnum}`, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success(t('common.delete_success', "Deleted successfully"));
            navigate(`/jp${locationPath.search}`); 
        } catch (err) {
            toast.error(t('common.delete_fail', "Delete failed: ") + (err.response?.data?.detail || "Unknown error"));
        }
    }
  };

  const FontControls = () => (
    <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ml-4">
      <button onClick={() => changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
      <div className="flex items-center gap-1 min-w-[40px] justify-center"><Type size={14} /><span className="font-black text-[10px]">{fontSize}</span></div>
      <button onClick={() => changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
    </div>
  );

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] text-[11px] overflow-hidden font-bold uppercase tracking-tighter ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <UniversalLookup {...lookup} onClose={() => setLookup({ ...lookup, isOpen: false })} onSelect={handleLookupSelect} />
      
      {/* --- Top Navigation Bar --- */}
      <div className="bg-[#1a3353] text-white p-2 flex justify-between items-center shadow-lg px-4">
        <div className={`flex items-center gap-4 ${isRTL ? 'mr-2' : 'ml-2'}`}>
          <button onClick={() => navigate(`/jp${locationPath.search}`)} className="hover:bg-white/10 p-1.5 rounded-full transition-all">
           {isRTL ? <ChevronRight size={18} /> : <ArrowLeft size={18}/>}
          </button>
          {!isNew && (
          <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
            <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
              <span className="text-orange-400 font-black">{nav.current_pos}</span> / {nav.total}
            </div>
            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
          </div>
          )}
        </div>

        <div className={`flex items-center gap-1 ${isRTL ? 'ml-2' : 'mr-2'}`}>
          {canSave && (
            <button onClick={handleSave} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-sm font-bold transition-all shadow-md">
              <Save size={14}/> {t('common.save', 'SAVE')}
            </button>
          )}
          
          <div className="w-[1px] h-6 bg-white/10 mx-1" />

          {canUpdate && (
            <button onClick={() => setShowStatusModal(true)} title={t('common.change_status', 'Change Status')} className="p-2 hover:bg-white/10 rounded-sm">
              <Tag size={16} className="text-yellow-400" />
            </button>
          )}

          <button onClick={() => setShowHistoryModal(true)} title={t('common.history', 'History')} className="p-2 hover:bg-white/10 rounded-sm">
            <History size={16} className="text-blue-300" />
          </button>

          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200">
            <Paperclip size={16} className="text-gray-300" />
            {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
          </button>

          {!isNew && canDelete && !isStatusReadOnly && (
            <button onClick={handleDelete} title={t('common.delete', 'Delete')} className="p-2 hover:bg-red-600 rounded-sm transition-colors text-red-400 hover:text-white">
              <Trash2 size={16} />
            </button>
          )}
          <FontControls />
        </div>
      </div>

      {/* --- Header Bar --- */}
      <div className="bg-white p-3 border-b flex justify-between items-center shadow-sm z-20">
        <div className={`flex gap-6 items-center ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <div className={`flex flex-col border-gray-200 ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
            <label className="text-[0.9em] text-gray-400 font-black italic">{t('jp.fields.jpnum', 'JOB PLAN')}</label>
            <input className="w-32 outline-none font-black text-blue-900 text-[0.9em] bg-transparent" value={data.jpnum} disabled={!isNew} onChange={e => setData({...data, jpnum: e.target.value})} />
          </div>
          
          <div className={`flex flex-col border-gray-200 ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
            <label className="text-[0.9em] text-gray-400 font-black italic">{t('jp.fields.status', 'STATUS')}</label>
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full animate-pulse shadow-sm ${data.status === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
              <span className="font-black text-blue-900 text-[0.9em]">{data.status}</span>
            </div>
          </div>

          <div className="flex flex-col">
            <label className="text-[0.9em] text-gray-400 font-black italic">{t('jp.fields.description', 'DESCRIPTION')} {canSave && '*'}</label>
            <input 
              className="w-[400px] outline-none font-bold text-blue-700 bg-transparent border-b border-transparent focus:border-blue-500 text-[0.9em]" 
              value={data.description} 
              disabled={isReadOnly}
              onChange={e => setData({...data, description: e.target.value})} 
            />
          </div>
        </div>
      </div>

      {/* --- Main Navigation Tabs --- */}
      <div className="flex bg-[#1a3353] text-white shadow-md">
        {[
          { id: 'Job Plan', label: t('jp.tabs.job_plan', 'Job Plan'), icon: <Briefcase size={14}/> },
          { id: 'Plans', label: t('jp.tabs.plans', 'Plans'), icon: <FileText size={14}/> },
          { id: 'Assets', label: t('jp.tabs.assets', 'Assets'), icon: <Box size={14}/> }
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} 
            className={`px-10 py-3 flex items-center gap-2 border-white/5 transition-all tracking-widest ${isRTL ? 'border-l' : 'border-r'}
            ${activeTab === t.id ? 'bg-white text-[#1a3353]' : 'hover:bg-white/10 opacity-60'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* --- Content Area --- */}
      <div className="flex-1 overflow-y-auto p-4 bg-[#f8f9fb] custom-scrollbar">
        {activeTab === 'Job Plan' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500">
            <div className="col-span-8 space-y-6">
              <Section title={t('jp.sections.details', 'Plan Details')}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <DataField label={t('jp.fields.template_type', 'Template Type')} value={data.template_type} isReadOnly={isReadOnly} onSearch={() => handleSearch('template_type', 'Select Type', 'DOMAIN')} />
                  <DataField label={t('jp.fields.duration', 'Duration (Hrs)')} value={data.duration} isReadOnly={isReadOnly} onChange={v => setData({...data, duration: v})} />
                  <DataField label={t('jp.fields.supervisor', 'Supervisor')} value={data.supervisor} isReadOnly={isReadOnly} onSearch={() => handleSearch('supervisor', 'Select Supervisor', 'labors')} />
                  <DataField label={t('jp.fields.class', 'Class Structure')} value={data.classstructureid} isReadOnly={isReadOnly} onSearch={() => handleSearch('classstructureid', 'Classification', 'classstructure')} />
                  <DataField label={t('jp.fields.safety_plan', 'Safety Plan')} value={data.safetypnum} isReadOnly={isReadOnly} onSearch={() => handleSearch('safetypnum', 'Select Safety Plan', 'safetyplans')} onChange={v => setData({...data, safetypnum: v})} />
                </div>
              </Section>
            </div>
            <div className="col-span-4">
              <Section title={t('jp.sections.responsibility', 'Responsibility')}>
                <div className="space-y-4">
                  <StaticField label={t('common.org', 'Organization')} value={data.org_id} />
                  <StaticField label={t('common.site', 'Site')} value={data.site_id} />
                  <StaticField label={t('jp.fields.revision', 'Revision')} value={data.revision} isHighlight />
                </div>
              </Section>
            </div>
          </div>
        )}

        {activeTab === 'Plans' && (
          <div className="max-w-7xl mx-auto bg-white rounded shadow-xl overflow-hidden min-h-[500px] flex flex-col">
            <div className="flex bg-gray-50 border-b">
              {[
                {id: 'Tasks', label: t('jp.subtabs.tasks', 'Tasks'), icon: <List size={12}/>}, 
                {id: 'Labor', label: t('jp.subtabs.labor', 'Labor'), icon: <UserCheck size={12}/>}, 
                {id: 'Materials', label: t('jp.subtabs.materials', 'Materials'), icon: <Package size={12}/>}, 
                {id: 'Tools', label: t('jp.subtabs.tools', 'Tools'), icon: <Wrench size={12}/>}
              ].map(st => (
                <button key={st.id} onClick={() => setPlanSubTab(st.id)} 
                  className={`px-10 py-3 text-[10px] font-black transition-all flex items-center gap-2
                  ${planSubTab === st.id ? 'bg-white border-t-4 border-t-blue-900 text-blue-900' : 'text-gray-400 hover:text-blue-700'}`}>
                  {st.icon} {st.label}
                </button>
              ))}
            </div>
            
            <div className="p-4 flex-1">
               <DynamicTable 
                 tab={planSubTab} 
                 data={data} 
                 isReadOnly={isReadOnly} 
                 isRTL={isRTL}
                 t={t}
                 onAdd={() => addSubRow(planSubTab.toLowerCase())} 
                 onSearch={handleSearch} 
                 onUpdate={updateSubRow} 
                 onRemove={(idx) => {
                  if (isReadOnly) return;
                  const newList = [...data[planSubTab.toLowerCase()]];
                  newList.splice(idx, 1);
                  setData({...data, [planSubTab.toLowerCase()]: newList});
               }} />
            </div>
          </div>
        )}

        {activeTab === 'Assets' && (
           <div className="max-w-7xl mx-auto bg-white rounded shadow-xl p-4 animate-in slide-in-from-bottom">
              <div className="flex justify-between items-center mb-4 border-b pb-2">
                 <h3 className="text-blue-900 font-black flex items-center gap-2"><Box size={16}/> {t('jp.sections.woassets', 'Work Assets and Safety Plans')}</h3>
                 {!isReadOnly && (
                   <button onClick={() => addSubRow('woassets')} className="bg-blue-900 text-white px-4 py-1.5 rounded-sm text-[9px] flex items-center gap-1 hover:bg-black transition-all shadow-md">
                     <Plus size={12}/> {t('common.new_row', 'NEW ROW')}
                   </button>
                 )}
              </div>
              <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-slate-50 text-gray-400 text-[9px] border-b font-black">
                  <tr>
                    <th className="p-2 border-x border-white">{t('common.location', 'Location')}</th>
                    <th className="p-2 border-x border-white">{t('common.asset', 'Asset')}</th>
                    <th className="p-2 border-x border-white">{t('common.site', 'Site')}</th>
                    <th className="p-2 w-10 border-x border-white"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.woassets.map((row, idx) => (
                    <tr key={idx} className="hover:bg-blue-50 transition-colors">
                      <td className="p-2 border-x border-gray-50 flex justify-between items-center">
                        <span className="text-blue-900 font-bold">{row.location || '---'}</span>
                        {!isReadOnly && <Search size={12} className="text-blue-500 cursor-pointer hover:scale-110" onClick={() => handleSearch('row_asset_loc', 'Select Location', 'locations', idx)} />}
                      </td>
                      <td className="p-2 border-x border-gray-50">
                         <div className="flex justify-between items-center">
                            <span className="text-blue-900 font-bold">{row.assetnum || '---'}</span>
                            {!isReadOnly && <Search size={12} className="text-blue-500 cursor-pointer hover:scale-110" onClick={() => handleSearch('row_asset_num', 'Select Asset', 'assets', idx)} />}
                         </div>
                      </td>
                      <td className="p-2 border-x border-gray-50 text-gray-500 font-bold">{row.site_id}</td>
                      <td className="p-2 text-center border-x border-white">
                        {!isReadOnly && (
                          <Trash2 size={14} className="text-gray-300 hover:text-red-500 cursor-pointer mx-auto transition-colors" onClick={() => {
                            const newList = [...data.woassets];
                            newList.splice(idx, 1);
                            setData({...data, woassets: newList});
                          }}/>
                        )}
                      </td>
                    </tr>
                  ))}
                  {data.woassets.length === 0 && (
                    <tr><td colSpan="4" className="p-8 text-center text-gray-300 italic">{t('common.no_records', 'No records found')}</td></tr>
                  )}
                </tbody>
              </table>
           </div>
        )}
      </div>

      {/* Modals */}
      {showStatusModal && (
      <StatusModal isOpen={showStatusModal} currentStatus={data.status} recordId={data.jpnum} appName="JOBPLAN" onClose={() => {setShowStatusModal(false); loadData(); }} />)}
      {showHistoryModal && (
      <StatusHistoryModal isOpen={showHistoryModal} recordId={data.jpnum} appName="JOBPLAN" onClose={() => setShowHistoryModal(false)} />)}
      {isDocModalOpen && (
      <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="JOBPLAN" ownerid={data.jpnum} />)}
    </div>
  );
};

// --- Reusable Components ---
const Section = ({ title, children }) => (
  <div className="bg-white border rounded shadow-sm">
    <div className="p-2 bg-slate-50 border-b font-black text-slate-500 uppercase text-[0.9em] tracking-widest flex items-center gap-2">
      <div className="w-1.5 h-3 bg-blue-600 rounded-full"></div> {title}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const DataField = ({ label, value, onSearch, onChange, isReadOnly }) => (
  <div className="flex flex-col border-b border-gray-100 py-1.5 group px-1 transition-all">
    <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-tighter italic">{label}</label>
    <div className="flex justify-between items-center h-5 mt-0.5">
      {onChange ? (
        <input 
          className="font-black text-blue-900 bg-transparent outline-none w-full disabled:opacity-70" 
          value={value} 
          disabled={isReadOnly}
          onChange={e => onChange(e.target.value)} 
        />
      ) : (
        <span className="font-black text-blue-900 opacity-90">{value || '---'}</span>
      )}
      {(onSearch && !isReadOnly) && <Search size={12} className="text-blue-400 cursor-pointer hover:scale-110 transition-transform" onClick={onSearch} />}
    </div>
  </div>
);

const StaticField = ({ label, value, isHighlight }) => (
  <div className="flex flex-col border-b border-gray-100 py-1.5">
    <label className="text-[0.8em] font-black text-gray-400 uppercase italic">{label}</label>
    <div className={`font-black ${isHighlight ? 'text-red-600 text-[0.1em]' : 'text-blue-900 opacity-70'}`}>{value || '---'}</div>
  </div>
);

const DynamicTable = ({ tab, data, isReadOnly, isRTL, t, onAdd, onSearch, onUpdate, onRemove }) => {
  const rows = data[tab.toLowerCase()] || [];
  return (
    <div className="animate-in fade-in duration-300">
      <div className={`flex mb-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
        {!isReadOnly && (
          <button onClick={onAdd} className="bg-blue-900 text-white px-4 py-1.5 rounded-sm text-[9px] font-black flex items-center gap-1 hover:bg-black transition-all shadow-md">
            <Plus size={12}/> {t('common.new_row', 'NEW ROW')}
          </button>
        )}
      </div>
      <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-slate-50 text-gray-400 text-[0.9em] border-b font-black uppercase">
          <tr>
            {tab === 'Tasks' && <><th className="p-2 border-x border-white w-20">{t('jp.table.seq', 'Seq')}</th><th className="p-2 border-x border-white">{t('jp.fields.description', 'Description')}</th><th className="p-2 border-x border-white text-center w-24">{t('jp.table.duration', 'Duration')}</th></>}
            {tab === 'Labor' && <><th className="p-2 border-x border-white">{t('jp.table.craft', 'Craft')}</th><th className="p-2 border-x border-white">{t('jp.table.labor', 'Labor')}</th><th className="p-2 border-x border-white text-center w-24">{t('jp.table.qty', 'Qty')}</th><th className="p-2 border-x border-white text-center w-24">{t('jp.table.hours', 'Hours')}</th><th className="p-2 border-x border-white text-center w-24">{t('jp.table.rate', 'Rate')}</th></>}
            {tab === 'Materials' && <><th className="p-2 border-x border-white">{t('jp.table.item', 'Item')}</th><th className="p-2 border-x border-white text-center w-32">{t('jp.table.qty', 'Qty')}</th><th className="p-2 border-x border-white text-center w-32">{t('jp.table.unitcost', 'Unit Cost')}</th></>}
            {tab === 'Tools' && <><th className="p-2 border-x border-white">{t('jp.table.tool', 'Tool')}</th><th className="p-2 border-x border-white text-center w-32">{t('jp.table.hours', 'Hours')}</th></>}
            <th className="w-10 border-x border-white"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-blue-50 text-[0.9em] transition-colors">
              {tab === 'Tasks' && (
                <>
                  <td className="p-2 border-x border-gray-50 text-blue-900 font-black">{row.task_sequence}</td>
                  <td className="p-2 border-x border-gray-50">
                    <input className="w-full bg-transparent outline-none text-blue-800 disabled:opacity-70 font-bold" disabled={isReadOnly} value={row.description} onChange={e => onUpdate('tasks', idx, 'description', e.target.value)} />
                  </td>
                  <td className="p-2 border-x border-gray-50 text-center">
                    <input className="w-full text-center bg-transparent outline-none text-blue-800 disabled:opacity-70 font-bold" type="number" disabled={isReadOnly} value={row.duration} onChange={e => onUpdate('tasks', idx, 'duration', e.target.value)} />
                  </td>
                </>
              )}
              {tab === 'Labor' && (
                <>
                  <td className="p-1 border-x border-gray-50">
                    <div className="flex justify-between items-center px-2">
                      <span className="font-black text-blue-900">{row.craft || '---'}</span>
                      {!isReadOnly && <Search size={12} className="text-blue-400 cursor-pointer hover:scale-110" onClick={() => onSearch('row_craft_role', 'Select Craft', 'crafts', idx)} />}
                    </div>
                  </td>
                  <td className="p-1 border-x border-gray-50">
                    <div className="flex justify-between items-center px-2">
                      <span className="font-black text-blue-900">{row.laborcode || '---'}</span>
                      {!isReadOnly && <Search size={12} className="text-blue-400 cursor-pointer hover:scale-110" onClick={() => onSearch('row_labor_role', 'Select Labor', 'labors', idx)} />}
                    </div>
                  </td>
                  <td className="p-1 border-x border-gray-50">
                    <input type="number" disabled={isReadOnly} className="w-full text-center bg-transparent outline-none text-blue-800 disabled:opacity-70 font-bold" value={row.quantity} onChange={e => onUpdate('labor', idx, 'quantity', e.target.value)} />
                  </td>
                  <td className="p-1 border-x border-gray-50">
                    <input type="number" disabled={isReadOnly} className="w-full text-center bg-transparent outline-none text-blue-800 disabled:opacity-70 font-bold" value={row.laborhrs} onChange={e => onUpdate('labor', idx, 'laborhrs', e.target.value)} />
                  </td>
                  <td className="p-1 border-x border-gray-50">
                    <input type="number" disabled={isReadOnly} className="w-full text-center bg-transparent outline-none text-blue-800 disabled:opacity-70 font-bold" value={row.laborrate} onChange={e => onUpdate('labor', idx, 'laborrate', e.target.value)} />
                  </td>                  
                </>
              )}
              {tab === 'Materials' && (
                <>
                  <td className="p-2 border-x border-gray-50 flex justify-between items-center font-bold text-blue-900">
                    {row.itemnum || '---'} {!isReadOnly && <Search size={12} className="text-blue-500 cursor-pointer hover:scale-110" onClick={() => onSearch('row_material_item', 'Select Item', 'items', idx)} />}
                  </td>
                  <td className="p-2 border-x border-gray-50 text-center">
                    <input className="w-full text-center bg-transparent disabled:opacity-70 font-bold text-blue-800 outline-none" disabled={isReadOnly} type="number" value={row.quantity} onChange={e => onUpdate('materials', idx, 'quantity', e.target.value)} />
                  </td>
                  <td className="p-2 border-x border-gray-50 text-center font-mono text-blue-800">{row.unitcost}</td>
                </>
              )}
              {tab === 'Tools' && (
                <>
                  <td className="p-2 border-x border-gray-50 flex justify-between items-center font-bold text-blue-900">
                    {row.itemnum || '---'} {!isReadOnly && <Search size={12} className="text-blue-500 cursor-pointer hover:scale-110" onClick={() => onSearch('row_tool_num', 'Select Tool', 'tools', idx)} />}
                  </td>
                  <td className="p-2 border-x border-gray-50 text-center">
                    <input className="w-full text-center bg-transparent disabled:opacity-70 font-bold text-blue-800 outline-none" disabled={isReadOnly} type="number" value={row.toolhrs} onChange={e => onUpdate('tools', idx, 'toolhrs', e.target.value)} />
                  </td>
                </>
              )}
              <td className="p-2 text-center border-x border-white">
                {!isReadOnly && (
                  <Trash2 size={14} className="text-gray-300 hover:text-red-500 cursor-pointer mx-auto transition-colors" onClick={() => onRemove(idx)}/>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
             <tr><td colSpan="6" className="p-8 text-center text-gray-300 italic">{t('common.no_records', 'No records found')}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default JPDetails;